import type { CreateAdminProjectDto, UpdateAdminProjectDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminProjectInput,
  V2UpdateAdminProjectInput
} from '@idc-forms/schemas/api-v2/admin-projects'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { projectsAdminV2Service } from '../services/projects-admin-v2.service'

/**
 * Mutaciones de proyectos contra la API v2. Identifican por id público (`prj_`) y,
 * al completar, invalidan todo el árbol `['projects']` (listado + summary +
 * detalle). El editor de página completa no existe: el alta/edición vive en el
 * side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateProjectV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminProjectInput) =>
      await projectsAdminV2Service.create(accessToken, body as CreateAdminProjectDto),
    onSuccess: () => {
      toast.success('Proyecto creado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateProjectV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminProjectInput }) =>
      await projectsAdminV2Service.update(accessToken, id, body as UpdateAdminProjectDto),
    onSuccess: () => {
      toast.success('Proyecto actualizado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteProjectV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await projectsAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Proyecto eliminado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
